import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import parseISO from 'date-fns/parseISO';
import { IS_QUALITYLODGINGS, QL_EVENTS } from '../../constants/analyticsEvents';
import { type AnalyticsBookingItem, type AnalyticsOrder } from '../../hooks/analytics/useAnalyticsCheckout';

/**
 * qualitylodgings GTM emitters. Each is a no-op when `!IS_QUALITYLODGINGS`, so the brand split lives
 * here in one place rather than scattered across trigger sites. All payloads use the new flat shape
 * (`hotel_name`, `room_name`, `location`, `dates`, `guests`, `duration`, `value`) — no `ecommerce`.
 * `value` (trip price) is sent on all events except `view_hotel_be` and `search_availability_be`.
 */

interface ViewBooking {
    arrivalDate?: string;
    departureDate?: string;
    amountAdults?: number;
    amountChildren?: number;
    amountYouths?: number;
}

const pushQlEvent = (event: GTMQualitylodgingsEvent) => {
    window.dataLayer = window.dataLayer ?? [];
    window.dataLayer.push(event);
};

/**
 * Fire `event` at most once per browser session, keyed by `key`. Mirrors the dedupe used by
 * `pushPurchaseEvent` so mount-based events survive component remounts / payment-gateway round-trips.
 */
const pushQlEventOnce = (key: string, event: GTMQualitylodgingsEvent) => {
    if (typeof window === 'undefined' || window.sessionStorage.getItem(key)) {
        return;
    }
    window.sessionStorage.setItem(key, '1');
    pushQlEvent(event);
};

const buildGuests = (booking: Pick<ViewBooking, 'amountAdults' | 'amountChildren' | 'amountYouths'>): number =>
    (booking.amountAdults ?? 0) + (booking.amountChildren ?? 0) + (booking.amountYouths ?? 0);

const buildDates = (arrivalDate?: string, departureDate?: string): string => `${arrivalDate} - ${departureDate}`;

const buildDuration = (arrivalDate?: string, departureDate?: string): number | undefined =>
    arrivalDate && departureDate ? differenceInCalendarDays(parseISO(departureDate), parseISO(arrivalDate)) : undefined;

const hasDatesAndGuests = (b: ViewBooking): boolean => !!b.arrivalDate && !!b.departureDate && buildGuests(b) > 0;

const getMainBooking = (summary: AnalyticsOrder): AnalyticsBookingItem | undefined =>
    summary.orderItems.find((item): item is AnalyticsBookingItem => item.__typename === 'Booking');

interface CheckoutPayload {
    hotel_name?: string;
    room_name?: string;
    location?: string;
    dates: string;
    guests: number;
    duration?: number;
    value: number;
    code: string;
}

const buildCheckoutPayload = (summary: AnalyticsOrder): CheckoutPayload | null => {
    const booking = getMainBooking(summary);
    if (!booking?.rentalUnit) {
        return null;
    }
    const { rentalUnit, arrivalDate, departureDate } = booking;
    return {
        hotel_name: rentalUnit.accommodation.name,
        room_name: rentalUnit.name,
        location: rentalUnit.accommodation.place ?? undefined,
        dates: buildDates(arrivalDate, departureDate),
        guests: buildGuests(booking),
        duration: buildDuration(arrivalDate, departureDate),
        value: summary.orderItems.find(Boolean)?.totalPrice ?? 0,
        code: rentalUnit.code,
    };
};

export const emitQlRoomView = (input: {
    accommodationName?: string;
    roomName: string;
    booking?: ViewBooking;
    value?: number;
}) => {
    if (!IS_QUALITYLODGINGS) {
        return;
    }
    const { accommodationName, roomName, booking, value } = input;
    pushQlEvent({
        event: QL_EVENTS.viewRoom,
        hotel_name: accommodationName,
        room_name: roomName,
        ...(booking && hasDatesAndGuests(booking)
            ? {
                  dates: buildDates(booking.arrivalDate, booking.departureDate),
                  guests: buildGuests(booking),
              }
            : {}),
        ...(value !== undefined ? { value } : {}),
    });
};

/** `view_hotel_be` — views an accommodation page. hotel_name only. */
export const emitQlHotelView = (input: { accommodationName?: string }) => {
    if (!IS_QUALITYLODGINGS) {
        return;
    }
    pushQlEvent({
        event: QL_EVENTS.viewHotelBe,
        hotel_name: input.accommodationName,
    });
};

/** `select_room` — user selects a room from a search results list. hotel_name, room_name, dates, guests, value. */
export const emitQlSelectRoom = (input: {
    accommodationName?: string;
    roomName?: string;
    booking?: ViewBooking;
    value?: number;
}) => {
    if (!IS_QUALITYLODGINGS) {
        return;
    }
    const { accommodationName, roomName, booking, value } = input;
    if (!booking || !hasDatesAndGuests(booking)) {
        return;
    }
    pushQlEvent({
        event: QL_EVENTS.selectRoom,
        hotel_name: accommodationName,
        room_name: roomName,
        dates: buildDates(booking.arrivalDate, booking.departureDate),
        guests: buildGuests(booking),
        ...(value !== undefined ? { value } : {}),
    });
};

/**
 * `search_availability_be` — a rental-unit availability search: results rendering on an accommodation
 * page, or a user picking dates in the calendar on a rental-unit detail page. hotel_name, room_name
 * (when a single unit is in context), dates, guests.
 */
export const emitQlSearchAvailability = (input: {
    accommodationName?: string;
    roomName?: string;
    booking?: ViewBooking;
}) => {
    if (!IS_QUALITYLODGINGS) {
        return;
    }
    const { accommodationName, roomName, booking } = input;
    if (!booking || !hasDatesAndGuests(booking)) {
        return;
    }
    pushQlEvent({
        event: QL_EVENTS.searchAvailabilityBe,
        hotel_name: accommodationName,
        room_name: roomName,
        dates: buildDates(booking.arrivalDate, booking.departureDate),
        guests: buildGuests(booking),
    });
};

/** `start_checkout` — starts checkout (CheckoutExtras). Full flat payload, once per session. */
export const emitQlStartCheckout = (summary?: AnalyticsOrder) => {
    if (!IS_QUALITYLODGINGS || !summary) {
        return;
    }
    const payload = buildCheckoutPayload(summary);
    if (!payload) {
        return;
    }
    const { code, ...event } = payload;
    pushQlEventOnce(`${QL_EVENTS.startCheckout}_${code}`, { event: QL_EVENTS.startCheckout, ...event });
};

/** `view_guest_data` — 2nd checkout page (enter guest info). Full flat payload, once per session. */
export const emitQlViewGuestData = (summary?: AnalyticsOrder) => {
    if (!IS_QUALITYLODGINGS || !summary) {
        return;
    }
    const payload = buildCheckoutPayload(summary);
    if (!payload) {
        return;
    }
    const { code, ...event } = payload;
    pushQlEventOnce(`${QL_EVENTS.viewGuestData}_${code}`, { event: QL_EVENTS.viewGuestData, ...event });
};

/** `view_summary` — Summary (payment) page. Full flat payload, once per session. */
export const emitQlViewSummary = (summary?: AnalyticsOrder) => {
    if (!IS_QUALITYLODGINGS || !summary) {
        return;
    }
    const payload = buildCheckoutPayload(summary);
    if (!payload) {
        return;
    }
    const { code, ...event } = payload;
    pushQlEventOnce(`${QL_EVENTS.viewSummary}_${code}`, { event: QL_EVENTS.viewSummary, ...event });
};

/** `booking_complete` — booking done, on the confirmation page. Full flat payload, once per session. */
export const emitQlBookingComplete = (summary?: AnalyticsOrder) => {
    if (!IS_QUALITYLODGINGS || !summary) {
        return;
    }
    const payload = buildCheckoutPayload(summary);
    if (!payload) {
        return;
    }
    const { code, ...event } = payload;
    pushQlEventOnce(`${QL_EVENTS.bookingComplete}_${code}`, { event: QL_EVENTS.bookingComplete, ...event });
};
